import { useState, useCallback, useRef } from 'react'
import { useNavigate } from 'react-router-dom'

import { PayPalButtons, usePayPalScriptReducer } from '@paypal/react-paypal-js'

import { PageName } from '@/enums/pageName'
import { PaymentMethod, PaymentPlatform } from '@/enums/payments'
import { getPath } from '@/utils/urlUtils'

import { Spinner } from '@/components/spinner'

import { ApiService } from '@/api'
import { useAuth } from '@/auth/useAuth'
import { resetContactStateCheckTimeout } from '@/commonPages/paymentScreenPage/hooks/contactState/useCheckContactState'
import { useSession } from '@/core/session/useSession'
import { trackPaymentInfoAdded, trackPaymentClick, trackPurchaseStarted } from '@/events/events'

import { TPayPalButton } from './types'

function getResultUrl(paymentResultUrl: string, successPage: PageName): URL {
  const fail = PageName.paymentFailed
  const result = paymentResultUrl.replace('--processor--', 'paypal')
  const url = new URL(result)
  url.searchParams.append('success_page', successPage)
  url.searchParams.append('fail_page', fail)
  url.searchParams.append('payment_method', PaymentMethod.PAYPAL)

  return url
}

export const PayPalButton = ({
  pageName,
  paypalButtonConfigStyle,
  productData,
  setItem,
  config,
  customTagline,
  successRedirectTo,
}: TPayPalButton) => {
  const { user } = useAuth()
  const sessionContext = useSession()
  const navigate = useNavigate()
  const [{ isPending }] = usePayPalScriptReducer()
  const [isPaypalInitialCheckoutEventIsSent, setPaypalInitialCheckoutEventIsSent] = useState(false)
  const { currency, price } = productData
  const semaphore = useRef<boolean>(false)
  const paymentUrl = useRef<string | undefined>(undefined)

  const createSubscription = useCallback<() => Promise<string>>(async () => {
    if (semaphore.current) {
      return Promise.reject()
    }
    resetContactStateCheckTimeout(setItem)

    const payload = {
      page_set_id: config.PAGE_SET_ID,
      plan_id: productData.internalPlanId,
    }
    semaphore.current = true
    try {
      const response = await ApiService.subscribePayPalPlan(payload)

      semaphore.current = false
      paymentUrl.current = response.data.payment_result_url

      return response.data.subscription_id
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e: any) {
      semaphore.current = false
      paymentUrl.current = undefined
      if (e.response?.status === 409) {
        navigate(getPath(successRedirectTo))
      } else {
        console.error('e', e)
        navigate(getPath(PageName.paymentFailed))
      }
      return Promise.reject(e)
    }
  }, [config, productData, navigate, setItem, successRedirectTo])

  const redirectToResultPage = useCallback<
    (subscriptionId: string | null | undefined, eventPromise: Promise<void>) => boolean
  >(
    (subscriptionId: string | null | undefined, eventPromise: Promise<void>): boolean => {
      if (!subscriptionId || !paymentUrl.current) {
        navigate(getPath(PageName.paymentFailed))
        return false
      }

      const url = getResultUrl(paymentUrl.current, successRedirectTo)

      url.searchParams.append('subscription_id', subscriptionId)
      eventPromise.finally(() => {
        window.location.href = url.href
      })
      return true
    },
    [paymentUrl, navigate, successRedirectTo]
  )

  const handlePaypalButtonCLick = async () => {
    if (!isPaypalInitialCheckoutEventIsSent) {
      setItem('paymentMethod', PaymentMethod.PAYPAL)
      setItem('paymentPlatform', PaymentPlatform.PAYPAL)
      trackPaymentInfoAdded(
        pageName,
        config.PARTNER_ID,
        config.APP_ID,
        config.PAGE_SET_ID,
        productData.payPalPlanId,
        PaymentMethod.PAYPAL,
        PaymentPlatform.PAYPAL,
        sessionContext,
        config.PIXEL_ID,
        config.TWITTER_CONF?.pixel_id,
        config.TIKTOK_PIXEL_ID,
        user,
        [],
        ['paypal']
      )
      trackPaymentClick(
        pageName,
        config.PARTNER_ID,
        config.APP_ID,
        config.PAGE_SET_ID,
        productData.payPalPlanId,
        PaymentMethod.PAYPAL,
        PaymentPlatform.PAYPAL,
        sessionContext,
        config.PIXEL_ID,
        config.TWITTER_CONF?.pixel_id,
        config.TIKTOK_PIXEL_ID,
        user
      )
      setPaypalInitialCheckoutEventIsSent(true)
    }
  }

  return (
    <>
      {customTagline && <div className="paypal-button-custom-tagline">{customTagline}</div>}
      {isPending && <Spinner />}
      <PayPalButtons
        style={{ ...paypalButtonConfigStyle.style }}
        disabled={false}
        forceReRender={[price, currency]}
        fundingSource={undefined}
        onClick={handlePaypalButtonCLick}
        onError={() => {}}
        createSubscription={createSubscription}
        onApprove={async (data, actions) => {
          const eventPromise = trackPurchaseStarted(
            pageName,
            config.PARTNER_ID,
            config.APP_ID,
            config.PAGE_SET_ID,
            productData.internalPlanId,
            PaymentMethod.PAYPAL,
            PaymentPlatform.PAYPAL,
            sessionContext,
            user
          )
          setItem(PageName.paymentScreen, 'paypal')

          try {
            await actions.order?.capture()
            if (!redirectToResultPage(data.subscriptionID, eventPromise)) {
              navigate(getPath(successRedirectTo))
            }
          } catch (error) {
            redirectToResultPage(data.subscriptionID, eventPromise)
            if (!redirectToResultPage(data.subscriptionID, eventPromise)) {
              navigate(getPath(PageName.paymentFailed))
            }
          }
        }}
      />
    </>
  )
}
